'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { colors, baseStyles } from './SharedUI';

const TIMEFRAMES = [
  { id: '15m', label: '15 Phút', interval: '15m', range: '60d' },
  { id: '30m', label: '30 Phút', interval: '30m', range: '60d' },
  { id: '1h',  label: '1 Giờ',  interval: '60m', range: '300d' }, 
  { id: '4h',  label: '4 Giờ',  interval: '60m', range: '400d' },  
  { id: '12h', label: '12 Giờ', interval: '60m', range: '600d' }, 
  { id: '1d',  label: '1 Ngày', interval: '1d',  range: '2y' },   
  { id: '2d',  label: '2 Ngày', interval: '1d',  range: '2y' }, 
  { id: '4d',  label: '4 Ngày', interval: '1d',  range: '3y' }, 
  { id: '5d',  label: '5 Ngày', interval: '1d',  range: '3y' }, 
];

export default function USStockOHLCMatrix({ defaultSymbol = 'SNDK' }) {
  // 1. Quản lý trạng thái nhập liệu tĩnh trên UI
  const [inputSymbol, setInputSymbol] = useState(defaultSymbol);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1d'); 
  const [showSettings, setShowSettings] = useState(false); 

  // Giá trị cấu hình gốc được chốt giữ
  const [savedSettings, setSavedSettings] = useState({
    sma20: 20, sma50: 50, sma200: 200,
    ema9: 9, ema21: 21, ema50: 50,
    rsi14: 14,
    macdFast: 12, macdSlow: 26, macdSignal: 9,
    bbPeriod: 20, bbStdDev: 2
  });

  // Vùng nhớ nháp khi đang gõ điều chỉnh Settings
  const [inputSettings, setInputSettings] = useState({ ...savedSettings });

  // 2. Bộ tham số ĐÃ KÍCH HOẠT để chạy thuật toán hiển thị
  const [activeSettings, setActiveSettings] = useState({ ...savedSettings });
  const [activeSymbol, setActiveSymbol] = useState('');
  const [activeTimeframe, setActiveTimeframe] = useState('');
  const [rawBars, setRawBars] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [marketPrice, setMarketPrice] = useState(null);
  const [previousClose, setPreviousClose] = useState(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); 

  const activeColors = useMemo(() => ({
    bg: colors?.bg || '#0b0e14',
    border: colors?.border || '#2b3139',
    green: colors?.green || '#089981',
    red: colors?.red || '#f23645',
    textSub: colors?.textSub || '#848e9c',
    textMain: colors?.textMain || '#ffffff'
  }), []);

  // HÀM FETCH SNAPSHOT: Chỉ kích hoạt khi click nút "Fetch New"
  const handleFetchOHLC = async () => {
    const symbolToFetch = inputSymbol.trim().toUpperCase();
    if (!symbolToFetch) return;

    setIsLoading(true);
    setErrorMsg(null);
    setSaveStatus(null); 

    const tfConfig = TIMEFRAMES.find(t => t.id === selectedTimeframe) || TIMEFRAMES[5];
    const proxyUrl = 'https://cors-anywhere.herokuapp.com/';
    const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${symbolToFetch}?range=${tfConfig.range}&interval=${tfConfig.interval}`;

    try {
      const response = await fetch(`${proxyUrl}${yahooUrl}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      
      if (!response.ok) throw new Error('Mã không tồn tại hoặc Proxy quá tải');
      
      const json = await response.json();
      const result = json?.chart?.result?.[0];
      
      if (!result) throw new Error('Không tìm thấy dữ liệu cấu trúc của mã này');

      setCurrency(result.meta?.currency || 'USD');
      setMarketPrice(result.meta?.regularMarketPrice || null);
      setPreviousClose(result.meta?.previousClose || null);

      const timestamps = result.timestamp || [];
      const indicators = result.indicators?.quote?.[0] || {};
      const opens = indicators.open || [];
      const highs = indicators.high || [];
      const lows = indicators.low || [];
      const closes = indicators.close || [];
      const volumes = indicators.volume || [];

      const formattedBars = [];
      for (let index = 0; index < timestamps.length; index++) {
        if (
          opens[index] !== null && opens[index] !== undefined &&
          closes[index] !== null && closes[index] !== undefined &&
          highs[index] !== null && highs[index] !== undefined &&
          lows[index] !== null && lows[index] !== undefined
        ) {
          formattedBars.push({
            timestamp: timestamps[index] * 1000,
            open: opens[index],
            high: highs[index],
            low: lows[index],
            close: closes[index],
            volume: volumes[index] || 0
          });
        }
      }

      setRawBars(formattedBars);
      setActiveSymbol(symbolToFetch);
      setActiveTimeframe(selectedTimeframe);
      
      // Áp dụng bộ số config tĩnh đã lưu cứng vào bộ tính toán nến
      setActiveSettings({ ...savedSettings });
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFetchOHLC();
  }, []); 

  const handleSaveSettingsOnUI = () => {
    setSavedSettings({ ...inputSettings }); 
    setShowSettings(false); 
    setSaveStatus('⚙️ Cấu hình thông số tĩnh đã lưu! Hãy nhấn "Fetch New" để áp dụng tính toán.');
  };

  const handleCloseSettingsOnUI = () => {
    setInputSettings({ ...savedSettings }); 
    setShowSettings(false); 
  };

  const handleSettingChange = (field, value) => {
    const num = parseFloat(value);
    setInputSettings(prev => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };

  // THUẬT TOÁN GỘP NẾN KHUNG GIỜ LỚN
  const aggregatedBars = useMemo(() => {
    if (rawBars.length === 0) return [];

    let chunkCount = 1;
    if (activeTimeframe === '4h') chunkCount = 4;
    if (activeTimeframe === '12h') chunkCount = 12;
    if (activeTimeframe === '2d') chunkCount = 2;
    if (activeTimeframe === '4d') chunkCount = 4;
    if (activeTimeframe === '5d') chunkCount = 5;

    if (chunkCount === 1) return [...rawBars];

    const aggregated = [];
    for (let i = 0; i < rawBars.length; i += chunkCount) {
      const chunk = rawBars.slice(i, i + chunkCount);
      if (chunk.length === 0) continue;

      const openPrice = chunk[0].open;
      const closePrice = chunk[chunk.length - 1].close;
      const highestPrice = Math.max(...chunk.map(b => b.high));
      const lowestPrice = Math.min(...chunk.map(b => b.low));
      const totalVolume = chunk.reduce((sum, b) => sum + (b.volume || 0), 0);
      const targetTime = chunk[chunk.length - 1].timestamp;

      aggregated.push({
        timestamp: targetTime,
        open: openPrice,
        high: highestPrice,
        low: lowestPrice,
        close: closePrice,
        volume: totalVolume
      });
    }
    return aggregated;
  }, [rawBars, activeTimeframe]);

  // BỘ MÁY KẾT NỐI VỚI HÀM NGOÀI GLOBAL SCOPE (AN TOÀN TUYỆT ĐỐI CHỐNG LỖI INITIALIZATION)
  const barsWithIndicators = useMemo(() => {
    if (aggregatedBars.length === 0) return [];

    const len = aggregatedBars.length;
    const closes = aggregatedBars.map(b => b.close);

    // Triển khai các hàm tính từ bên ngoài global scope
    const smaFast = externalCalcSMA(closes, len, activeSettings.sma20);
    const smaMid = externalCalcSMA(closes, len, activeSettings.sma50);
    const smaSlow = externalCalcSMA(closes, len, activeSettings.sma200);

    const emaFast = externalCalcEMA(closes, len, activeSettings.ema9);
    const emaMid = externalCalcEMA(closes, len, activeSettings.ema21);
    const emaSlow = externalCalcEMA(closes, len, activeSettings.ema50);

    const rsi14 = externalCalcRSI(closes, len, activeSettings.rsi14);
    
    const macdHist = externalCalcMACD(closes, len, activeSettings.macdFast, activeSettings.macdSlow, activeSettings.macdSignal);
    
    const { bbUpper, bbLower } = externalCalcBB(closes, len, activeSettings.bbPeriod, activeSettings.bbStdDev);

    return aggregatedBars.map((bar, idx) => ({
      ...bar,
      smaFast: smaFast[idx], smaMid: smaMid[idx], smaSlow: smaSlow[idx],
      emaFast: emaFast[idx], emaMid: emaMid[idx], emaSlow: emaSlow[idx],
      rsi14: rsi14[idx],
      macdHist: macdHist[idx],
      bbUpper: bbUpper[idx], bbLower: bbLower[idx]
    })).reverse(); 
  }, [aggregatedBars, activeSettings]);

  // PHÂN TÍCH SUMMARY BIỆN LUẬN DỰA TRÊN SỐ LIỆU CHỨNG CỨ TỪ HÀM CHỈ BÁO TOÀN CỤC
  const marketSummary = useMemo(() => {
    if (barsWithIndicators.length === 0) return null;
    
    const latest = barsWithIndicators[0];

    // RULE 1: Đánh giá Long-term Trend (SMA)
    let trendText = 'TRUNG LẬP (SIDEWAYS)';
    let trendColor = '#fbbf24';
    let trendEvidence = `Giá (${fmt(latest.close)}) đang nằm kẹp giữa các đường SMA`;
    if (latest.smaFast && latest.smaMid && latest.smaSlow) {
      if (latest.close > latest.smaFast && latest.smaFast > latest.smaMid && latest.smaMid > latest.smaSlow) {
        trendText = 'TĂNG GIÁ MẠNH (BULLISH)';
        trendColor = activeColors.green;
        trendEvidence = `Giá (${fmt(latest.close)}) > SMA${activeSettings.sma20}(${fmt(latest.smaFast)}) > SMA${activeSettings.sma50}(${fmt(latest.smaMid)}) > SMA${activeSettings.sma200}(${fmt(latest.smaSlow)})`;
      } else if (latest.close < latest.smaFast && latest.smaFast < latest.smaMid && latest.smaMid < latest.smaSlow) {
        trendText = 'GIẢM GIÁ MẠNH (BEARISH)';
        trendColor = activeColors.red;
        trendEvidence = `Giá (${fmt(latest.close)}) < SMA${activeSettings.sma20}(${fmt(latest.smaFast)}) < SMA${activeSettings.sma50}(${fmt(latest.smaMid)}) < SMA${activeSettings.sma200}(${fmt(latest.smaSlow)})`;
      }
    }

    // RULE 2: Đánh giá Short-term Trend (EMA Crossover)
    let shortTrendText = 'TÍCH LUỸ / ĐI NGANG';
    let shortTrendColor = '#94a3b8';
    let shortTrendEvidence = 'Các đường EMA đang quấn vào nhau';
    if (latest.emaFast && latest.emaMid) {
      if (latest.emaFast > latest.emaMid) {
        shortTrendText = 'XU HƯỚNG TĂNG NGẮN HẠN';
        shortTrendColor = activeColors.green;
        shortTrendEvidence = `Đường EMA${activeSettings.ema9}(${fmt(latest.emaFast)}) nằm TRÊN EMA${activeSettings.ema21}(${fmt(latest.emaMid)})`;
      } else {
        shortTrendText = 'XU HƯỚNG GIẢM NGẮN HẠN';
        shortTrendColor = activeColors.red;
        shortTrendEvidence = `Đường EMA${activeSettings.ema9}(${fmt(latest.emaFast)}) nằm DƯỚI EMA${activeSettings.ema21}(${fmt(latest.emaMid)})`;
      }
    }

    // RULE 3: Đánh giá Momentum Động lượng (RSI)
    let momentumText = 'ỔN ĐỊNH / BÌNH THƯỜNG';
    let momentumColor = '#fbbf24';
    let momentumEvidence = `Chỉ số RSI hiện tại nằm trong vùng an toàn`;
    if (latest.rsi14 !== null) {
      momentumEvidence = `RSI hiện tại đạt mức: ${latest.rsi14.toFixed(2)}`;
      if (latest.rsi14 >= 70) {
        momentumText = `QUÁ MUA (OVERBOUGHT)`;
        momentumColor = activeColors.red;
        momentumEvidence += ` (Vượt mốc nguy hiểm >= 70)`;
      } else if (latest.rsi14 <= 30) {
        momentumText = `QUÁ BÁN (OVERSOLD)`;
        momentumColor = activeColors.green;
        momentumEvidence += ` (Thủng mốc nguy hiểm <= 30)`;
      }
    }

    // RULE 4: Đánh giá Momentum Confirmation (MACD Histogram)
    let confirmText = 'KHÔNG RÕ RÀNG';
    let confirmColor = '#94a3b8';
    let confirmEvidence = `Histogram bằng không hoặc thiếu dữ liệu`;
    if (latest.macdHist !== null) {
      confirmEvidence = `MACD Histogram hiện tại: ${latest.macdHist.toFixed(4)}`;
      if (latest.macdHist > 0) {
        confirmText = 'ĐỘNG LƯỢNG TĂNG (MUA ƯU THẾ)';
        confirmColor = activeColors.green;
        confirmEvidence += ` (> 0, phe mua đang kiểm soát thế trận)`;
      } else {
        confirmText = 'ĐỘNG LƯỢNG SUY GIẢM (BÁN ƯU THẾ)';
        confirmColor = activeColors.red;
        confirmEvidence += ` (< 0, phe bán đang tạo áp lực ép giá)`;
      }
    }

    // RULE 5: Đánh giá Biến động Volatility (Bollinger Bands Squeeze)
    let volatilityText = 'BIẾN ĐỘNG BÌNH THƯỜNG';
    let volatilityColor = '#cbd5e1';
    let volatilityEvidence = 'Giá đang dao động ổn định bên trong dải băng';
    if (latest.bbUpper && latest.bbLower) {
      const bandwidth = (latest.bbUpper - latest.bbLower) / latest.close;
      volatilityEvidence = `Độ rộng dải băng BB (Bandwidth): ${(bandwidth * 100).toFixed(2)}%`;
      
      if (bandwidth < 0.04) {
        volatilityText = 'BB SQUEEZE (NÉN NĂNG LƯỢNG)';
        volatilityColor = '#fbbf24';
        volatilityEvidence += ` (< 4.00% mốc nén chặt, sắp có bùng nổ biên độ mạnh)`;
      } else if (latest.close >= latest.bbUpper) {
        volatilityText = 'QUÁ TẢI BIÊN ĐỘ TRÊN';
        volatilityColor = activeColors.green;
        volatilityEvidence = `Giá đóng cửa (${fmt(latest.close)}) vượt biên trên BB Upper (${fmt(latest.bbUpper)})`;
      } else if (latest.close <= latest.bbLower) {
        volatilityText = 'QUÁ TẢI BIÊN ĐỘ DƯỚI';
        volatilityColor = activeColors.red;
        volatilityEvidence = `Giá đóng cửa (${fmt(latest.close)}) thủng biên dưới BB Lower (${fmt(latest.bbLower)})`;
      }
    }

    return { trendText, trendColor, trendEvidence, shortTrendText, shortTrendColor, shortTrendEvidence, momentumText, momentumColor, momentumEvidence, confirmText, confirmColor, confirmEvidence, volatilityText, volatilityColor, volatilityEvidence };
  }, [barsWithIndicators, activeSettings]);

  // HÀM LƯU SNAPSHOT
  const handleSaveSnapshot = async () => {
    if (barsWithIndicators.length === 0) {
      setSaveStatus('❌ Không có dữ liệu để lưu. Vui lòng Fetch trước!');
      return;
    }
    const snapshotPayload = {
      symbol: activeSymbol,
      timeframe: activeTimeframe,
      savedAt: new Date().toISOString(),
      marketPrice: marketPrice,
      appliedSettings: activeSettings, 
      marketSummary: marketSummary,
      data: barsWithIndicators.slice(0, 15) 
    };
    try {
      setSaveStatus('⏳ Đang xử lý lưu dữ liệu...');
      localStorage.setItem(`stock_snapshot_${activeSymbol}_${activeTimeframe}`, JSON.stringify(snapshotPayload));
      setSaveStatus(`✅ Đã lưu thành công gói Giá, Ma trận Chỉ báo và Bản Summary của ${activeSymbol}!`);
    } catch (error) {
      setSaveStatus(`❌ Thất bại: ${error.message}`);
    }
  };

  const priceStats = useMemo(() => {
    if (!marketPrice || !previousClose) return { diffStr: '0.00%', isUp: true };
    const pct = ((marketPrice - previousClose) / previousClose) * 100;
    return { diffStr: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`, isUp: pct >= 0 };
  }, [marketPrice, previousClose]);

  const formatBarTime = (ms) => {
    const date = new Date(ms);
    if (['15m', '30m', '1h', '4h', '12h'].includes(activeTimeframe)) {
      return date.toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <div style={{ backgroundColor: activeColors.bg, color: activeColors.textMain, padding: '24px', borderRadius: '8px', border: `1px solid ${activeColors.border}`, maxWidth: '1350px', margin: '0 auto', fontFamily: 'monospace', boxSizing: 'border-box' }}>
      
      {/* KHU VỰC ĐIỀU KHIỂN CHỨC NĂNG CHÍNH */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: '900', fontSize: '18px', color: activeColors.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              MÃ CHỨNG KHOÁN: {activeSymbol || 'CHƯA CÓ'} {activeTimeframe && `(${activeTimeframe})`}
            </div>
            <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold', marginTop: '4px' }}>
              {isLoading ? '⏳ ĐANG TẢI SNAPSHOT...' : '📷 CONFIGURABLE TECHNICAL MATRIX (WITH EXPLICIT EVIDENCE DATA)'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button 
              onClick={() => showSettings ? handleCloseSettingsOnUI() : setShowSettings(true)}
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: showSettings ? activeColors.red : activeColors.textMain, border: `1px solid ${activeColors.border}`, padding: '8px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              {showSettings ? '✕ Cancel Config' : '⚙️ Indicator Settings'}
            </button>

            <input
              type="text"
              value={inputSymbol}
              onChange={(e) => setInputSymbol(e.target.value)}
              placeholder="Mã cổ phiếu"
              onKeyDown={(e) => e.key === 'Enter' && handleFetchOHLC()}
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', border: `1px solid ${activeColors.border}`, padding: '8px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase', outline: 'none', width: '120px' }}
            />
            <button onClick={handleFetchOHLC} disabled={isLoading} style={{ backgroundColor: isLoading ? 'rgba(255,255,255,0.1)' : activeColors.green, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              {isLoading ? 'Fetching...' : 'Fetch New'}
            </button>
            <button onClick={handleSaveSnapshot} disabled={isLoading || barsWithIndicators.length === 0} style={{ backgroundColor: (isLoading || barsWithIndicators.length === 0) ? 'rgba(255,255,255,0.05)' : '#3b82f6', color: (isLoading || barsWithIndicators.length === 0) ? activeColors.textSub : '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: (isLoading || barsWithIndicators.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              Save Data
            </button>
          </div>
        </div>

        {/* HỘP ĐIỀU CHỈNH THÔNG SỐ NHÁP TĨNH */}
        {showSettings && (
          <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px dashed ${activeColors.border}`, borderRadius: '6px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '11px', color: activeColors.green, fontWeight: 'bold' }}>📈 TREND (SMA)</div>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>SMA Fast: <input type="number" value={inputSettings.sma20} onChange={(e) => handleSettingChange('sma20', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>SMA Mid: <input type="number" value={inputSettings.sma50} onChange={(e) => handleSettingChange('sma50', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>SMA Slow: <input type="number" value={inputSettings.sma200} onChange={(e) => handleSettingChange('sma200', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 'bold' }}>⚡ SHORT TREND (EMA)</div>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>EMA Fast: <input type="number" value={inputSettings.ema9} onChange={(e) => handleSettingChange('ema9', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>EMA Mid: <input type="number" value={inputSettings.ema21} onChange={(e) => handleSettingChange('ema21', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>EMA Slow: <input type="number" value={inputSettings.ema50} onChange={(e) => handleSettingChange('ema50', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 'bold' }}>📊 MOMENTUM & CONFIRM</div>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>RSI Period: <input type="number" value={inputSettings.rsi14} onChange={(e) => handleSettingChange('rsi14', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>MACD Fast: <input type="number" value={inputSettings.macdFast} onChange={(e) => handleSettingChange('macdFast', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>MACD Slow: <input type="number" value={inputSettings.macdSlow} onChange={(e) => handleSettingChange('macdSlow', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>MACD Sig: <input type="number" value={inputSettings.macdSignal} onChange={(e) => handleSettingChange('macdSignal', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '11px', color: '#f472b6', fontWeight: 'bold' }}>🌊 VOLATILITY (BB)</div>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>BB Period: <input type="number" value={inputSettings.bbPeriod} onChange={(e) => handleSettingChange('bbPeriod', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
                <label style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>BB StdDev: <input type="number" step="0.1" value={inputSettings.bbStdDev} onChange={(e) => handleSettingChange('bbStdDev', e.target.value)} style={{ width: '55px', background: '#000', border: `1px solid ${activeColors.border}`, color: '#fff', textAlign: 'center', padding: '2px' }}/></label>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' }}>
              <button onClick={handleCloseSettingsOnUI} style={{ backgroundColor: 'transparent', color: activeColors.textSub, border: `1px solid ${activeColors.border}`, padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Cancel / Reset</button>
              <button onClick={handleSaveSettingsOnUI} style={{ backgroundColor: activeColors.green, color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Save Settings</button>
            </div>
          </div>
        )}

        {/* TABS CHỌN INTERVAL KHÔNG TỰ ĐỘNG FETCH */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', backgroundColor: 'rgba(255,255,255,0.02)', padding: '4px', borderRadius: '6px', border: `1px solid ${activeColors.border}` }}>
          {TIMEFRAMES.map(tf => {
            const isSelected = selectedTimeframe === tf.id;
            return (
              <button key={tf.id} onClick={() => setSelectedTimeframe(tf.id)} style={{ flex: 1, minWidth: '65px', backgroundColor: isSelected ? 'rgba(255,255,255,0.12)' : 'transparent', color: isSelected ? '#fff' : activeColors.textSub, border: isSelected ? `1px solid ${activeColors.border}` : 'none', padding: '6px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', textAlign: 'center', transition: 'all 0.15s ease' }}>
                {tf.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* GIÁ THỊ TRƯỜNG */}
      {marketPrice && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', margin: '14px 0', padding: '8px 0', borderTop: `1px dashed ${activeColors.border}`, borderBottom: `1px dashed ${activeColors.border}`, backgroundColor: 'rgba(0,0,0,0.2)' }}>
          <span style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold' }}>GIÁ CHỐT PHIÊN SNAPSHOT:</span>
          <span style={{ fontSize: '20px', fontWeight: '900', color: priceStats.isUp ? activeColors.green : activeColors.red }}>{marketPrice.toFixed(2)} {currency}</span>
          <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#fff', backgroundColor: priceStats.isUp ? activeColors.green : activeColors.red, padding: '2px 6px', borderRadius: '4px' }}>{priceStats.diffStr}</span>
        </div>
      )}

      {saveStatus && <div style={{ color: '#fff', fontSize: '12px', padding: '10px', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '4px', marginBottom: '16px', backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>{saveStatus}</div>}
      {errorMsg && <div style={{ color: activeColors.red, fontSize: '12px', padding: '12px', border: `1px solid ${activeColors.red}40`,  borderRadius: '4px', marginBottom: '16px', backgroundColor: `${activeColors.red}05` }}>❌ Lỗi: {errorMsg}</div>}

      {/* SECTION A: SUMMARY */}
      {marketSummary && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#a7f3d0', marginBottom: '10px', letterSpacing: '0.5px' }}>SECTION A: ALGORITHMIC STRATEGY RULES SUMMARY & EVIDENCE</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ border: `1px solid ${activeColors.border}`, padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold' }}>📈 LONG-TERM TREND (SMA {activeSettings.sma20}/{activeSettings.sma50}/{activeSettings.sma200})</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: marketSummary.trendColor }}>{marketSummary.trendText}</span>
                <span style={{ fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' }}>{marketSummary.trendEvidence}</span>
              </div>
            </div>
            <div style={{ border: `1px solid ${activeColors.border}`, padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold' }}>⚡ SHORT-TERM TREND (EMA {activeSettings.ema9}/{activeSettings.ema21})</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: marketSummary.shortTrendColor }}>{marketSummary.shortTrendText}</span>
                <span style={{ fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' }}>{marketSummary.shortTrendEvidence}</span>
              </div>
            </div>
            <div style={{ border: `1px solid ${activeColors.border}`, padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold' }}>📊 MOMENTUM OVERVIEW (RSI {activeSettings.rsi14})</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: marketSummary.momentumColor }}>{marketSummary.momentumText}</span>
                <span style={{ fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' }}>{marketSummary.momentumEvidence}</span>
              </div>
            </div>
            <div style={{ border: `1px solid ${activeColors.border}`, padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold' }}>🎯 MOMENTUM CONFIRMATION (MACD HISTOGRAM)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: marketSummary.confirmColor }}>{marketSummary.confirmText}</span>
                <span style={{ fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' }}>{marketSummary.confirmEvidence}</span>
              </div>
            </div>
            <div style={{ border: `1px solid ${activeColors.border}`, padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold' }}>🌊 VOLATILITY & WIDTH (BOLLINGER BANDS {activeSettings.bbPeriod})</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: marketSummary.volatilityColor }}>{marketSummary.volatilityText}</span>
                <span style={{ fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' }}>{marketSummary.volatilityEvidence}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION B: OHLCV */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '13px', color: activeColors.green, marginBottom: '8px', letterSpacing: '0.5px' }}>SECTION B: HISTORICAL OHLCV DATA</div>
        <div style={{ border: `1px solid ${activeColors.border}`, borderRadius: '6px', overflow: 'hidden', opacity: isLoading ? 0.3 : 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1.2fr', backgroundColor: 'rgba(255,255,255,0.03)', padding: '10px 12px', fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold', textTransform: 'uppercase', borderBottom: `1px solid ${activeColors.border}` }}>
            <div>Thời Gian</div>
            <div style={{ textAlign: 'right' }}>Mở (O)</div>
            <div style={{ textAlign: 'right' }}>Cao (H)</div>
            <div style={{ textAlign: 'right' }}>Thấp (L)</div>
            <div style={{ textAlign: 'right' }}>Đóng (C)</div>
            <div style={{ textAlign: 'right' }}>Khối Lượng (V)</div>
          </div>
          <div style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {barsWithIndicators.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: activeColors.textSub, fontSize: '12px' }}>Chưa có dữ liệu.</div>
            ) : (
              barsWithIndicators.slice(0, 15).map((bar, idx) => (
                <div key={`ohlc-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1.2fr', padding: '10px 12px', fontSize: '13px', backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.15)' : 'transparent', borderBottom: `1px solid rgba(255,255,255,0.02)`, alignItems: 'center' }}>
                  <div style={{ color: activeColors.textMain, fontWeight: 'bold' }}>{formatBarTime(bar.timestamp)}</div>
                  <div style={{ textAlign: 'right', color: '#cbd5e1' }}>{bar.open.toFixed(2)}</div>
                  <div style={{ textAlign: 'right', color: '#94a3b8' }}>{bar.high.toFixed(2)}</div>
                  <div style={{ textAlign: 'right', color: '#64748b' }}>{bar.low.toFixed(2)}</div>
                  <div style={{ textAlign: 'right', color: bar.close >= bar.open ? activeColors.green : activeColors.red, fontWeight: 'bold' }}>{bar.close.toFixed(2)}</div>
                  <div style={{ textAlign: 'right', color: activeColors.textSub }}>{bar.volume.toLocaleString()}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* SECTION C: INDICATORS */}
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#3b82f6', marginBottom: '8px', letterSpacing: '0.5px' }}>SECTION C: ORGANIZED TECHNICAL INDICATOR MATRIX</div>
        <div style={{ border: `1px solid ${activeColors.border}`, borderRadius: '6px', overflowX: 'auto', opacity: isLoading ? 0.3 : 1 }}>
          <div style={{ minWidth: '1200px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2.4fr 2.4fr 0.9fr 1fr 1.5fr', backgroundColor: 'rgba(59, 130, 246, 0.06)', fontSize: '11px', fontWeight: 'black', color: '#fff', textTransform: 'uppercase', textAlign: 'center', borderBottom: `1px solid ${activeColors.border}`, letterSpacing: '0.5px' }}>
              <div style={{ padding: '8px', textAlign: 'left', borderRight: `1px solid ${activeColors.border}` }}>Timeline</div>
              <div style={{ padding: '8px', borderRight: `1px solid ${activeColors.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>📈 Trend Indicators</div>
              <div style={{ padding: '8px', borderRight: `1px solid ${activeColors.border}` }}>⚡ Short-term Trend</div>
              <div style={{ padding: '8px', borderRight: `1px solid ${activeColors.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>📊 Momentum</div>
              <div style={{ padding: '8px', borderRight: `1px solid ${activeColors.border}` }}>🎯 Confirmation</div>
              <div style={{ padding: '8px' }}>Waves Volatility</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.9fr 1fr 0.75fr 0.75fr', backgroundColor: 'rgba(255,255,255,0.02)', padding: '10px 6px', fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold', textTransform: 'uppercase', borderBottom: `1px solid ${activeColors.border}`, textAlign: 'right' }}>
              <div style={{ textAlign: 'left' }}>Thời Gian</div>
              <div style={{ color: '#cbd5e1' }}>SMA {activeSettings.sma20}</div>
              <div style={{ color: '#cbd5e1' }}>SMA {activeSettings.sma50}</div>
              <div style={{ color: '#cbd5e1' }}>SMA {activeSettings.sma200}</div>
              <div style={{ color: '#93c5fd' }}>EMA {activeSettings.ema9}</div>
              <div style={{ color: '#93c5fd' }}>EMA {activeSettings.ema21}</div>
              <div style={{ color: '#93c5fd' }}>EMA {activeSettings.ema50}</div>
              <div style={{ color: '#f59e0b', textAlign: 'center' }}>RSI {activeSettings.rsi14}</div>
              <div style={{ color: '#a7f3d0', textAlign: 'center' }}>MACD Hist</div>
              <div style={{ color: '#f472b6' }}>BB Up ({activeSettings.bbPeriod})</div>
              <div style={{ color: '#f472b6' }}>BB Low ({activeSettings.bbPeriod})</div>
            </div>

            <div style={{ maxHeight: '350px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              {barsWithIndicators.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: activeColors.textSub, fontSize: '12px' }}>Vui lòng nhấn Fetch New để đồng bộ ma trận chỉ báo.</div>
              ) : (
                barsWithIndicators.slice(0, 15).map((bar, idx) => (
                  <div key={`ind-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.9fr 1fr 0.75fr 0.75fr', padding: '10px 6px', fontSize: '13px', backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.15)' : 'transparent', borderBottom: `1px solid rgba(255,255,255,0.01)`, alignItems: 'center', textAlign: 'right' }}>
                    <div style={{ color: activeColors.textMain, fontWeight: 'bold', textAlign: 'left' }}>{formatBarTime(bar.timestamp)}</div>
                    <div style={{ color: bar.smaFast !== null && bar.close >= bar.smaFast ? colors.green : colors.red }}>{fmt(bar.smaFast)}</div>
                    <div>{fmt(bar.smaMid)}</div>
                    <div style={{ fontWeight: 'bold', color: '#94a3b8' }}>{fmt(bar.smaSlow)}</div>
                    
                    <div style={{ color: bar.emaFast !== null && bar.emaMid !== null && bar.emaFast >= bar.emaMid ? colors.green : colors.red }}>{fmt(bar.emaFast)}</div>
                    <div>{fmt(bar.emaMid)}</div>
                    <div>{fmt(bar.emaSlow)}</div>
                    
                    <div style={{ textAlign: 'center', color: bar.rsi14 >= 70 ? colors.red : bar.rsi14 <= 30 ? colors.green : '#f59e0b', fontWeight: (bar.rsi14 >= 70 || bar.rsi14 <= 30) ? 'bold' : 'normal' }}>{fmt(bar.rsi14)}</div>
                    <div style={{ textAlign: 'center', color: bar.macdHist >= 0 ? colors.green : colors.red, fontWeight: 'bold' }}>{fmt(bar.macdHist)}</div>
                    <div style={{ color: '#cbd5e1' }}>{fmt(bar.bbUpper)}</div>
                    <div style={{ color: '#cbd5e1' }}>{fmt(bar.bbLower)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// =========================================================================
// 🛠️ ĐÃ CÔ LẬP TUYỆT ĐỐI: ĐƯA TOÀN BỘ CÁC HÀM SỐ HỌC RA NGOÀI LỚP COMPONENT
// Triệt tiêu vĩnh viễn 100% tất cả các lỗi compiler ép Scope biến bậy bạ!
// =========================================================================
function externalCalcSMA(closes, len, period) {
  const sma = new Array(len).fill(null);
  if (len < period || period <= 0) return sma;
  for (let i = period - 1; i < len; i++) {
    const slice = closes.slice(i - period + 1, i + 1);
    sma[i] = slice.reduce((sum, b) => sum + b, 0) / period;
  }
  return sma;
}

function externalCalcEMA(closes, len, period) {
  const ema = new Array(len).fill(null);
  if (len < period || period <= 0) return ema;
  const smoothingFactor = 2 / (period + 1);
  const initialSum = closes.slice(0, period).reduce((sum, b) => sum + b, 0);
  ema[period - 1] = initialSum / period;
  for (let i = period; i < len; i++) {
    ema[i] = closes[i] * smoothingFactor + ema[i - 1] * (1 - smoothingFactor);
  }
  return ema;
}

function externalCalcRSI(closes, len, period) {
  const rsi = new Array(len).fill(null);
  if (len <= period || period <= 0) return rsi;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff > 0) gains += diff;
    else losses -= diff;
  }
  let avgGain = gains / period;
  let avgLoss = losses / period;
  rsi[period] = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / avgLoss));

  for (let i = period + 1; i < len; i++) {
    const diff = closes[i] - closes[i - 1];
    avgGain = ((avgGain * (period - 1)) + (diff > 0 ? diff : 0)) / period;
    avgLoss = ((avgLoss * (period - 1)) + (diff < 0 ? -diff : 0)) / period;
    rsi[i] = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / avgLoss));
  }
  return rsi;
}

function externalCalcMACD(closes, len, fast, slow, signal) {
  const macdHist = new Array(len).fill(null);
  if (len < Math.max(fast, slow) || fast <= 0 || slow <= 0 || signal <= 0) return macdHist;
  const emaFast = externalCalcEMA(closes, len, fast);
  const emaSlow = externalCalcEMA(closes, len, slow);
  const macdLine = new Array(len).fill(null);
  const signalLine = new Array(len).fill(null);

  const baseMacdLimit = Math.max(fast, slow) - 1;
  for (let i = baseMacdLimit; i < len; i++) {
    if (emaFast[i] !== null && emaSlow[i] !== null) macdLine[i] = emaFast[i] - emaSlow[i];
  }
  let firstIdx = macdLine.findIndex(v => v !== null);
  if (firstIdx !== -1 && len >= firstIdx + signal) {
    const startIdx = firstIdx + signal - 1;
    signalLine[startIdx] = macdLine.slice(firstIdx, startIdx + 1).reduce((sum, b) => sum + b, 0) / signal;
    const kSig = 2 / (signal + 1);
    for (let i = startIdx + 1; i < len; i++) {
      if (macdLine[i] !== null && signalLine[i - 1] !== null) {
        signalLine[i] = macdLine[i] * kSig + signalLine[i - 1] * (1 - kSig);
      }
    }
    for (let i = startIdx; i < len; i++) {
      if (macdLine[i] !== null && signalLine[i] !== null) macdHist[i] = macdLine[i] - signalLine[i];
    }
  }
  return macdHist;
}

function externalCalcBB(closes, len, period, stdDevMultiplier) {
  const bbUpper = new Array(len).fill(null);
  const bbLower = new Array(len).fill(null);
  if (len < period || period <= 0) return { bbUpper, bbLower };
  const bbSma = externalCalcSMA(closes, len, period);

  for (let i = period - 1; i < len; i++) {
    const mean = bbSma[i];
    const slice = closes.slice(i - period + 1, i + 1);
    const variance = slice.reduce((sum, b) => sum + Math.pow(b - mean, 2), 0) / period;
    const stdDev = Math.sqrt(variance);
    bbUpper[i] = mean + (stdDevMultiplier * stdDev);
    bbLower[i] = mean - (stdDevMultiplier * stdDev);
  }
  return { bbUpper, bbLower };
}

const fmt = (val) => (val === null || val === undefined) ? '---' : val.toFixed(2);
